import React, { useMemo } from "react"
import { Box, Flex, Text, Heading } from "@chakra-ui/react"

import { useCartContext } from "@app/providers/cart"
import { useShopify } from "@app/hooks/useShopify"
import { graphql, useStaticQuery } from "gatsby"
import ResolvedImage from "@app/components/ResolvedImage"

type Props = {
  drawer?: boolean
  /** In drawer mode, "scrollable" renders subtotal/discounts (in DrawerBody), "sticky" renders total/BNPL (in DrawerFooter). Ignored on desktop. */
  drawerSection?: "scrollable" | "sticky"
}

export const CartSummary: React.FC<Props> = React.memo(({ drawer = false, drawerSection }) => {
  const { cart, currencyCode, linesCount, warnings, subTotal, discountAllocations } = useCartContext()

  const { formatMoney } = useShopify()
  const { payInFour } = useStaticQuery<GatsbyTypes.StaticCartPayInFourAndStaticPaymentsQuery>(graphql`
    query StaticCartPayInFourAndStaticPayments {
      payInFour: sanityPageCart {
        payInFourEnabled
        payInFourSeparator
        payInFourTitle
        payInFourLogo: _rawPayInFourLogo(resolveReferences: { maxDepth: 2 })
      }
    }
  `)

  const payInFourSubtotal = useMemo(() => {
    const amount = Number(cart?.cost?.subtotalAmount?.amount || 0)
    const currencyCode = cart?.cost?.subtotalAmount?.currencyCode || "AUD"
    return { amount, currencyCode }
  }, [cart?.cost?.subtotalAmount])

  const cartTotal = useMemo(
    () => cart?.cost?.subtotalAmount?.amount && formatMoney(Number(cart?.cost?.subtotalAmount?.amount)),
    [formatMoney, cart?.cost?.subtotalAmount]
  )

  const isScrollable = drawer && drawerSection === "scrollable"
  const isSticky = drawer && drawerSection === "sticky"

  if (linesCount <= 0) return null

  return (
    <Box w="100%" px={0} pb={4}>
      <Box>
        {(!drawer || isScrollable) && (
          <>
            {warnings?.length
              ? warnings.map(({ message, target }) => {
                  return (
                    <Flex key={target} w="100%" alignItems="center" justifyContent="space-between">
                      <Text size="xl">{message}</Text>
                    </Flex>
                  )
                })
              : null}
            <Flex w="100%" alignItems="center" justifyContent="space-between">
              <Text size="md">
                Subtotal{" "}
                <Text size="sm" display="inline-block" ml={0.5}>
                  ({currencyCode})
                </Text>
              </Text>
              <Text size="md">{formatMoney(subTotal)}</Text>
            </Flex>
            {discountAllocations?.length > 0 && (
              <Flex direction="column">
                {discountAllocations.map((discount, index) => (
                  <Flex key={index} justify="space-between">
                    <Text size="md">
                      {discount.title}{" "}
                      <Text size="sm" display="inline-block" ml={0.5}>
                        ({currencyCode})
                      </Text>
                    </Text>
                    <Text size="md">-{formatMoney(discount.amount)}</Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </>
        )}

        {(!drawer || isSticky) && (
          <>
            <Flex w="100%" alignItems="center" justifyContent="space-between">
              <Heading size="h6" variant="medium" fontSize="1rem">
                Total{" "}
                <Text size="sm" display="inline-block" ml={0.5}>
                  ({currencyCode})
                </Text>
              </Heading>
              <Heading size="h6" variant="medium" fontSize="1rem">
                {cartTotal}
              </Heading>
            </Flex>
            {payInFour?.payInFourEnabled && (
              <Flex direction="column" gap={1} mt={2}>
                <Text size="md" color="primary.midGrey">
                  {payInFour?.payInFourSeparator}
                </Text>
                <Flex w="100%" alignItems="flex-start" justifyContent="space-between" direction="row">
                  <Flex direction="row" flexWrap="wrap" alignItems="center" gap={2}>
                    <Text size="md" color="primary.midGrey">
                      {payInFour.payInFourTitle}
                    </Text>
                    {payInFour.payInFourLogo && (
                      <Flex direction="row" gap={2} alignItems="center" w="fit-content">
                        {payInFour.payInFourLogo?.map((image: any) => (
                          <ResolvedImage key={image._key} image={image} pos="relative" height="16px" />
                        ))}
                      </Flex>
                    )}
                  </Flex>
                  <Text size="md" color="primary.midGrey" minW="max-content">
                    4 x {formatMoney(payInFourSubtotal.amount / 4, payInFourSubtotal.currencyCode)}
                  </Text>
                </Flex>
              </Flex>
            )}
          </>
        )}

        {!drawer && (
          <>
            <Flex w="100%" alignItems="center" mt={1} justifyContent="space-between">
              <Text size="md">Shipping</Text>
              <Text size="md">Calculated at checkout</Text>
            </Flex>
            <Box width="100%" height="1px" bg="primary.softGrey" my={{ base: 3.5, md: 6 }} />
            <Flex w="100%" alignItems="center" justifyContent="space-between">
              <Heading size="h6" variant="medium" fontSize="1rem">
                Total
              </Heading>
              <Heading size="h6" variant="medium" fontSize="1rem">
                {cartTotal}
              </Heading>
            </Flex>
            <Box width="100%" height="1px" bg="primary.softGrey" my={{ base: 3.5, md: 6 }} />
          </>
        )}
      </Box>
    </Box>
  )
})
